import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen,
  FileText,
  Video,
  Clock,
  CheckCircle,
  Lock,
  ArrowRight,
  Users,
  Calculator,
  Calendar,
  MessageCircle,
  Unlock,
  Wallet,
  ClipboardCheck,
  PiggyBank,
  Table,
  Mail,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import MentorshipBookingModal from "@/components/MentorshipBookingModal";
import TaxFilingModal from "@/components/TaxFilingModal";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";
import AuthModal from "@/components/AuthModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Courses = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [mentorshipModalOpen, setMentorshipModalOpen] = useState(false);
  const [taxFilingModalOpen, setTaxFilingModalOpen] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check if user is logged in (new auth system) OR has subscribed
    const userToken = localStorage.getItem("user_token");
    const hasSubscribed = localStorage.getItem("compoundr_subscribed");
    if (userToken || hasSubscribed) {
      setIsUnlocked(true);
    }
  }, []);

  // Handle auth success
  const handleAuthSuccess = (userData) => {
    localStorage.setItem("user_token", userData.token || "authenticated");
    localStorage.setItem("compoundr_subscribed", "true");
    setIsUnlocked(true);
    setShowAuth(false);
    window.location.reload();
  };

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }

    setNewsletterLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscribers`, {
        email: newsletterEmail,
        phone: "",
        source: "newsletter_courses",
      });
      setNewsletterSuccess(true);
      setNewsletterEmail("");
      toast.success("Welcome to the Compoundr newsletter! 🎉");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.success("You&apos;re already subscribed! We'll keep you updated.");
        setNewsletterSuccess(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  const freeGuides = [
    {
      title: "Welcome to Investing Starter Pack",
      description:
        "Your complete guide to getting started with investing: opening a bank account, getting your SIN, and setting up an investment account.",
      type: "PDF Guide",
      icon: BookOpen,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/ftyw4y4y_Compoundr_Investors_Guide_Final_v2.pdf",
    },
    {
      title: "TFSA Starter Guide",
      description:
        "Everything you need to know about Tax-Free Savings Accounts in Canada.",
      type: "PDF Guide",
      icon: PiggyBank,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/e6ky3w2i_TFSA_Starter_Guide%20%282%29.pdf",
    },
    {
      title: "Student Budget Template",
      description:
        "A simple spreadsheet to track your income, expenses, and investments.",
      type: "Spreadsheet",
      icon: Table,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_etf-advisor/artifacts/d0dp736a_Student_Budget_Template.xlsx",
    },
    {
      title: "First Investment Checklist",
      description:
        "Step-by-step checklist to make your first investment with confidence.",
      type: "Checklist",
      icon: ClipboardCheck,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_etf-advisor/artifacts/nc6jnk17_First_Investment_Checklist%20%284%29.pdf",
    },
  ];

  const booksBundle = [
    {
      title: "The Intelligent Investor",
      author: "Benjamin Graham",
      description: "The definitive book on value investing. A must-read for every serious investor.",
      filename: "The-Intelligent-Investor.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/6njhxix9_The%20Intelligent%20Investor%20-%20BENJAMIN%20GRAHAM.pdf",
    },
    {
      title: "The Psychology of Money",
      author: "Morgan Housel",
      description: "Timeless lessons on wealth, greed, and happiness. Perfect for beginners.",
      filename: "The-Psychology-of-Money.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/q09d5yma_The%20psycology%20of%20money.pdf",
    },
    {
      title: "Rich Dad Poor Dad",
      author: "Robert Kiyosaki",
      description: "Learn the mindset differences between the rich and the poor.",
      filename: "Rich-Dad-Poor-Dad.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/f1ynod4z_20150408135309188.pdf",
    },
    {
      title: "I Will Teach You to Be Rich",
      author: "Ramit Sethi",
      description: "A practical guide to managing money, investing, and building wealth in your 20s and 30s.",
      filename: "I-Will-Teach-You-To-Be-Rich.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/peta4loz_pdfcoffee.com-i-will-teach-you-to-be-rich.pdf",
    },
  ];

  const services = [
    {
      title: "One-on-One Mentorship",
      description:
        "Get a chance to meet an investment expert who will help you build your portfolio and guide you through your investing journey.",
      disclaimer: "(not investment advice)",
      icon: Users,
      color: "from-emerald-500 to-emerald-600",
      features: [
        "Meet with an investment expert",
        "Portfolio building guidance",
        "Goal setting & tracking",
        "Ongoing support via chat",
      ],
      cta: "Book a Session",
      available: true,
    },
    {
      title: "File Your Taxes",
      description:
        "We connect you to a premium accountant. A Certified CPA who will help you maximize your refund and ensure you claim all eligible credits.",
      icon: Calculator,
      color: "from-purple-500 to-purple-600",
      features: [
        "Certified CPA accountant",
        "Maximize your refund",
        "International student expertise",
        "Fast & accurate filing",
      ],
      cta: "Get Started",
      available: true,
    },
  ];

  const upcomingPrograms = [
    {
      title: "Compoundr Bootcamp",
      description:
        "A 4-week guided program to go from complete beginner to confident investor. Live sessions, community support, and personalized feedback.",
      duration: "4 weeks",
      format: "Live + Self-paced",
      status: "Coming Soon",
      features: [
        "Weekly live Q&A sessions",
        "Step-by-step video lessons",
        "Private community access",
        "1-on-1 portfolio review",
      ],
    },
    {
      title: "International Student Money Mastery",
      description:
        "Specifically designed for international students in Canada. Learn to navigate banking, investing, and building credit as a newcomer.",
      duration: "3 weeks",
      format: "Self-paced",
      status: "Coming Soon",
      features: [
        "Bank account setup guide",
        "Credit building strategies",
        "Currency exchange tips",
        "Tax basics for students",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="courses-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Home */}
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
            ← Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              Tools & Resources
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-700 mb-4">
              Products & Guides
            </h1>
            <p className="text-lg text-slate-600">
              Free resources to get started, and structured programs when you&apos;re
              ready to level up.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Free Guides */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Free Guides
            </h2>
            <p className="text-slate-600">
              Download these resources to start learning right away.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {freeGuides.map((guide, index) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full card-hover border-0 shadow-card bg-white">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <guide.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <Badge variant="secondary" className="mb-3">
                      {guide.type}
                    </Badge>
                    <h3 className="font-heading font-bold text-lg text-navy-700 mb-2">
                      {guide.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {guide.description}
                    </p>
                    {guide.downloadUrl ? (
                      isUnlocked ? (
                        <a 
                          href={guide.downloadUrl}
                          download
                          className="w-full"
                        >
                          <Button
                            variant="outline"
                            className="w-full"
                            data-testid={`download-${guide.title.toLowerCase().replace(/ /g, "-")}`}
                          >
                            Download Free
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                          onClick={() => setShowAuth(true)}
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Sign in to Download
                        </Button>
                      )
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        data-testid={`download-${guide.title.toLowerCase().replace(/ /g, "-")}`}
                      >
                        Coming Soon
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Books Bundle Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 lg:p-12 border border-amber-200"
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-4xl">📚</span>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  FREE Download
                </Badge>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-4">
                Investor&apos;s Book Bundle
              </h2>
              <p className="text-lg text-navy-700 font-medium mb-2">
                These are the books that have made most investors rich.
              </p>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Download all 4 essential investing books completely free. Start your wealth-building journey today!
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {booksBundle.map((book, index) => (
                <motion.div
                  key={book.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-5 shadow-sm border border-amber-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <h4 className="font-heading font-bold text-navy-700 text-sm mb-1">
                    {book.title}
                  </h4>
                  <p className="text-xs text-amber-600 mb-2">by {book.author}</p>
                  <p className="text-xs text-slate-500 mb-4">{book.description}</p>
                  {isUnlocked ? (
                    <a 
                      href={book.downloadUrl}
                      download={book.filename}
                      className="inline-flex items-center justify-center w-full h-9 px-3 text-sm font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                      Download Free
                    </a>
                  ) : (
                    <button 
                      onClick={() => setShowAuth(true)}
                      className="inline-flex items-center justify-center w-full h-9 px-3 text-sm font-medium rounded-md bg-slate-300 text-slate-500 hover:bg-slate-400 hover:text-slate-700 transition-colors"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      Sign in
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services - Mentorship & Tax Filing */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <Badge className="bg-purple-100 text-purple-700 mb-4">
              Premium Services
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Personalized Help When You Need It
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Sometimes you need more than guides. Get one-on-one support from our team.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-floating overflow-hidden">
                  <CardHeader className={`bg-gradient-to-r ${service.color} text-white`}>
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                      <service.icon className="w-7 h-7" />
                    </div>
                    <CardTitle className="text-2xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-slate-600 mb-2">{service.description}</p>
                    {service.disclaimer && (
                      <p className="text-slate-400 text-xs mb-6">{service.disclaimer}</p>
                    )}
                    {!service.disclaimer && <div className="mb-6" />}

                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 text-sm text-slate-600"
                        >
                          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    <Button
                      className={`w-full ${isUnlocked ? `bg-gradient-to-r ${service.color} hover:opacity-90 text-white` : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
                      data-testid={`service-${service.title.toLowerCase().replace(/ /g, "-")}`}
                      disabled={!isUnlocked}
                      onClick={() => {
                        if (!isUnlocked) return;
                        if (service.title === "One-on-One Mentorship") {
                          setMentorshipModalOpen(true);
                        } else if (service.title === "File Your Taxes") {
                          setTaxFilingModalOpen(true);
                        }
                      }}
                    >
                      {isUnlocked ? (
                        <>
                          {service.cta}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Sign in to Access
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wealthsimple Referral Section */}
      <section id="wealthsimple" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className={`bg-white rounded-2xl shadow-lg p-8 md:p-12 relative ${!isUnlocked ? 'overflow-hidden' : ''}`}>
              {/* Lock Overlay when not unlocked */}
              {!isUnlocked && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
                  <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">Unlock This Offer</h3>
                  <p className="text-slate-500 text-center max-w-sm mb-4">
                    Subscribe with your email on the homepage to unlock all exclusive content and offers.
                  </p>
                  <a href="/">
                    <Button className="bg-[#00C853] hover:bg-[#00B84D] text-white">
                      <Unlock className="w-4 h-4 mr-2" />
                      Go to Homepage to Unlock
                    </Button>
                  </a>
                </div>
              )}

              {/* Wealthsimple Logo */}
              <div className="flex justify-center mb-6">
                <div className={`w-16 h-16 ${isUnlocked ? 'bg-[#00C853]' : 'bg-slate-300'} rounded-2xl flex items-center justify-center`}>
                  <span className="text-white text-3xl font-bold">W</span>
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-navy-700 text-center mb-3">
                Get Free $25 Towards Your TFSA Account
              </h2>

              {/* Subheadline */}
              <p className="text-slate-600 text-center mb-8 max-w-xl mx-auto">
                Use my personal referral link. Yes, <strong>FREE $25</strong> deposited to your Wealthsimple TFSA in 1-2 business days.
              </p>

              {/* 3 Simple Steps */}
              <div className="bg-slate-50 rounded-xl p-6 mb-8">
                <p className="font-semibold text-navy-700 mb-4 text-center">3 Simple Steps:</p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 ${isUnlocked ? 'bg-[#00C853]' : 'bg-slate-300'} text-white rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
                      1
                    </div>
                    <div>
                      <p className="font-medium text-navy-700">Press the link</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 ${isUnlocked ? 'bg-[#00C853]' : 'bg-slate-300'} text-white rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
                      2
                    </div>
                    <div>
                      <p className="font-medium text-navy-700">Create account</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 ${isUnlocked ? 'bg-[#00C853]' : 'bg-slate-300'} text-white rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
                      3
                    </div>
                    <div>
                      <p className="font-medium text-navy-700">Deposit $1 and get free $25 towards your TFSA <span className="text-slate-500">(no scams)</span></p>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-center">
                  <span className="font-bold text-navy-700">💻 Pro Tip:</span>{" "}
                  <span className="text-slate-600">Use your laptop with any browser you&apos;re comfortable with. It&apos;s much easier to create an account on a computer!</span>
                </p>
              </div>

              {/* CTA Button */}
              <div className="text-center mb-6">
                <p className="text-slate-600 mb-4">Get free $25 towards your Wealthsimple TFSA account</p>
                {isUnlocked ? (
                  <Button 
                    onClick={() => setWealthsimpleModalOpen(true)}
                    className="bg-[#00C853] hover:bg-[#00B84D] text-white text-lg px-10 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                    data-testid="wealthsimple-cta"
                  >
                    Claim My Free $25
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    className="bg-slate-300 text-slate-500 text-lg px-10 py-6 rounded-xl cursor-not-allowed"
                    disabled
                  >
                    <Lock className="w-5 h-5 mr-2" />
                    Unlock to Claim $25
                  </Button>
                )}
              </div>

              {/* Trust Facts - Statistical info about Wealthsimple */}
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <p className="font-semibold text-navy-700 mb-4 text-center text-sm">Why Wealthsimple?</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <CheckCircle className={`w-4 h-4 ${isUnlocked ? 'text-[#00C853]' : 'text-slate-300'} flex-shrink-0`} />
                    <span>Canada&apos;s largest robo-advisor with $20B+ in assets under management</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <CheckCircle className={`w-4 h-4 ${isUnlocked ? 'text-[#00C853]' : 'text-slate-300'} flex-shrink-0`} />
                    <span>Regulated by Canadian securities authorities (IIROC member)</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <CheckCircle className={`w-4 h-4 ${isUnlocked ? 'text-[#00C853]' : 'text-slate-300'} flex-shrink-0`} />
                    <span>No account minimums or hidden fees</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <CheckCircle className={`w-4 h-4 ${isUnlocked ? 'text-[#00C853]' : 'text-slate-300'} flex-shrink-0`} />
                    <span>Used by 3+ million Canadians</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <CheckCircle className={`w-4 h-4 ${isUnlocked ? 'text-[#00C853]' : 'text-slate-300'} flex-shrink-0`} />
                    <span>Your investments are protected by CIPF coverage</span>
                  </div>
                </div>
              </div>

              {/* Trust Disclaimer */}
              <p className="text-xs text-slate-500 text-center">
                You&apos;ll be opening your account directly with Wealthsimple, a trusted and regulated Canadian investment platform. Compoundr. simply provides you with the opportunity to get started with a bonus.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Programs */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Upcoming Programs
            </h2>
            <p className="text-slate-600">
              Structured learning paths for when you&apos;re ready to go deeper.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {upcomingPrograms.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-card overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-navy-700 to-navy-600 text-white">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-white/20 text-white hover:bg-white/30">
                        {program.status}
                      </Badge>
                      <Lock className="w-5 h-5 opacity-50" />
                    </div>
                    <CardTitle className="text-2xl mt-4">{program.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-slate-600 mb-6">{program.description}</p>

                    <div className="flex gap-4 mb-6">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {program.duration}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Video className="w-4 h-4" />
                        {program.format}
                      </div>
                    </div>

                    <div className="space-y-2 mb-6">
                      <p className="text-sm font-medium text-navy-700">
                        What&apos;s included:
                      </p>
                      {program.features.map((feature, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-sm text-slate-600"
                        >
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    <Button
                      disabled
                      className="w-full bg-slate-100 text-slate-400"
                    >
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Newsletter */}
      <section className="py-16 bg-gradient-to-br from-emerald-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Join Our Newsletter
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Be the first to know when we host sessions, launch courses, and release more amazing financial tools.
            <span className="block mt-2 font-medium text-white">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Newsletter subscribers get exclusive early-bird discounts!
            </span>
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white/20 rounded-2xl p-6 max-w-md mx-auto"
            >
              <CheckCircle className="w-12 h-12 text-white mx-auto mb-3" />
              <p className="text-white font-medium text-lg">You&apos;re on the list!</p>
              <p className="text-emerald-100 text-sm mt-1">We&apos;ll notify you about new launches and exclusive deals.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 h-12 bg-white/90 border-0 text-slate-800 placeholder:text-slate-400"
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="h-12 bg-white text-emerald-600 hover:bg-emerald-50 px-8 font-semibold"
                data-testid="newsletter-submit"
              >
                {newsletterLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}
          
          <p className="text-emerald-200 text-xs mt-4">
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </section>

      
      
      {/* Modals - Must be at the end */}
      <MentorshipBookingModal 
        open={mentorshipModalOpen} 
        onOpenChange={setMentorshipModalOpen} 
      />
      <TaxFilingModal 
        open={taxFilingModalOpen} 
        onOpenChange={setTaxFilingModalOpen} 
      />
      <WealthsimpleStepsModal 
        open={wealthsimpleModalOpen} 
        onOpenChange={setWealthsimpleModalOpen} 
      />
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />
    </div>
  );
};

export default Courses;
